/* eslint-disable i18next/no-literal-string */
/* eslint no-param-reassign: [2, { "props": true, "ignorePropertyModificationsFor": ["properties"] }] */

import { pageDataService } from 'common/utilities/PageDataService';
import { localeIgnoreCaseEquals } from 'common/utilities/String';
import { createBrowserHistory, History } from 'history';

import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';

import { EnvironmentTelemetryPlugin } from './EnvironmentTelemetryPlugin';
import { toError } from './ErrorHelpers';
import { Exception, ScubaTagValue, ServiceName } from './TelemetryConstants';

export type UserActivityGroup = 'PageViewActivity' | 'ClickActivity' | 'AlertActivity' | 'UpsellActivity';

export type Properties = Record<string, string | number | boolean | undefined | null>;

export default class TelemetryProvider {
  public static get Instance(): TelemetryProvider {
    if (!this.instance) {
      this.instance = new this(createBrowserHistory(), false);
    }

    return this.instance;
  }

  public static init(history: History, logToConsole: boolean): void {
    TelemetryProvider.instance = new this(history, logToConsole);
  }

  // Properties excluded from casing change
  private static excludedProperties: Set<string> = new Set<string>([
    'name',
    'countryOrRegionCode',
    'traceNumber',
    'clientAppVersion',

    // Some properties as listed as precaution, they don't get picked up with other telemetry properties
    'traceIdentifier',
    'serviceName',
    'clientIp',
    'time',
  ]);

  private static instance: TelemetryProvider;

  private readonly appInsights: ApplicationInsights;

  private readonly logToConsole: boolean;

  private constructor(history: History, logToConsole: boolean) {
    const instrumentationKey = pageDataService.getAppInsightKey();
    const connectionString = pageDataService.getAppInsightConnectionString();
    const userData = pageDataService.getUserData();
    const reactPlugin = new ReactPlugin();
    const environmentPlugin = new EnvironmentTelemetryPlugin();

    this.logToConsole = logToConsole;
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString,
        instrumentationKey,
        disableFetchTracking: false,
        disableTelemetry: !connectionString && !instrumentationKey,
        extensions: [environmentPlugin, reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: { history },
        },
      },
    });

    this.appInsights.loadAppInsights();
    this.appInsights.setAuthenticatedUserContext(userData.userId);
    this.appInsights.trackPageView({
      uri: window.location.origin + window.location.pathname + pageDataService.getQueryStringForTelemetry(),
    });
  }

  /**
   * Log a custom event.
   *
   * Please use {@link logUserActivity} for logging user UI interactions.
   *
   * @param name A string to identify this event in the portal.
   * @param properties map[string, string] - additional data used to filter events and metrics in the portal. Defaults to empty.
   * @param measurements map[string, number] - metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
   * @param group Event group to log. Defaults to `Diagnostic`.
   */
  public trackEvent(name: string, properties?: Properties, measurements?: { [name: string]: number }, group = 'Diagnostic'): void {
    if (this.logToConsole) {
      console.info(`trackEvent ${name} ${JSON.stringify(properties)} ${JSON.stringify(measurements)}`);
    }

    const updatedProps = { EventName: name, EventGroup: group, ...properties };
    this.logAppInsightsTrackEvent(name, updatedProps, measurements);
  }

  /**
   * Log an exception you have caught.
   *
   * @param error         {Error}                      An exception from a catch clause.
   * @param eventName     {string}                     Optional name of the event. Defaults to {@link exception.name}.
   * @param properties    {{ [name: string]: string }} Additional data used to filter events and metrics in the portal. Defaults to empty.
   * @param measurements  {{ [name: string]: number }} Metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
   * @param severityLevel {SeverityLevel}              Severity level.
   * @param id            {string}                     Optional unique GUID identifying this error.
   */
  public trackException(
    error: Error,
    eventName?: string,
    properties?: Properties,
    measurements?: { [name: string]: number },
    severityLevel?: SeverityLevel,
    id?: string
  ): void {
    if (this.logToConsole) {
      console.error(
        `trackException ${error.name} ${error.message} ${error.stack} ${id} ${JSON.stringify(properties)} ${JSON.stringify(
          measurements
        )}`
      );
    }

    const props = {
      ...properties,
      EventName: eventName ?? error.name,
      EventGroup: Exception,
      ExceptionCode: id,
      ExceptionType: error.name,
      ExceptionMessage: error.message,
      Tag: ScubaTagValue,
      serviceName: ServiceName,
    };
    const updatedProps = properties === undefined ? props : this.telemetryPropertiesCheck(props);

    this.appInsights.trackException({
      exception: error,
      id,
      properties: updatedProps,
      measurements,
      severityLevel,
    });
  }

  /**
   * Log an exception where type is unknown.
   *
   * @param exception     {unknown}                    An exception from a catch clause.
   * @param eventName     {string}                     Optional name of the event. Defaults to {@link exception.name}.
   * @param properties    {{ [name: string]: string }} Additional data used to filter events and metrics in the portal. Defaults to empty.
   * @param measurements  {{ [name: string]: number }} Metrics associated with this event, displayed in Metrics Explorer on the portal. Defaults to empty.
   * @param severityLevel {SeverityLevel}              Severity level.
   * @param id            {string}                     Optional unique GUID identifying this error.
   */
  public trackUnknownException(
    exception: unknown,
    eventName?: string,
    properties?: Properties,
    measurements?: { [name: string]: number },
    severityLevel?: SeverityLevel,
    id?: string
  ): void {
    this.trackException(toError(exception), eventName, properties, measurements, severityLevel, id);
  }

  /**
   * Log a numeric value that is not associated with a specific event. Typically used to send regular reports of performance indicators.
   * To send a single measurement, use just the first two parameters. If you take measurements very frequently, you can reduce the
   * telemetry bandwidth by aggregating multiple measurements and sending the resulting average at intervals.
   *
   * @param name A string that identifies the metric.
   * @param average Number representing either a single measurement, or the average of several measurements.
   * @param sampleCount The number of measurements represented by the average. Defaults to 1.
   * @param min The smallest measurement in the sample. Defaults to the average.
   * @param max The largest measurement in the sample. Defaults to the average.
   */
  public trackMetric(
    name: string,
    average: number,
    sampleCount?: number,
    min?: number,
    max?: number,
    properties?: { [name: string]: string }
  ): void {
    if (this.logToConsole) {
      console.info(`trackMetric ${name} ${average} ${sampleCount} ${min} ${max} ${JSON.stringify(properties)}`);
    }

    const updatedProps = properties === undefined ? properties : this.telemetryPropertiesCheck(properties);
    this.appInsights.trackMetric({ name, average, sampleCount, min, max, properties: updatedProps });
  }

  /**
   * Log a diagnostic message.
   *
   * @param message A message string
   * @param properties map[string, string] - additional data used to filter traces in the portal. Defaults to empty.
   */
  public trackTrace(message: string, properties?: { [name: string]: string }): void {
    if (this.logToConsole) {
      console.info(`trackTrace ${message} ${JSON.stringify(properties)}`);
    }

    const updatedProps = properties === undefined ? properties : this.telemetryPropertiesCheck(properties);
    this.appInsights.trackTrace({ message, properties: updatedProps });
  }

  /**
   * Immediately send all queued telemetry.
   */
  public flush(): void {
    this.appInsights.flush();
  }

  /**
   * Logs the user action
   *
   * @param name       {string}                     A string to identify this event in the portal.
   * @param group      {UserActivityGroup}          Type of user action.
   * @param properties {{ [name: string]: string }} Additional data used to filter events and metrics in the portal. Defaults to empty.
   */
  public logUserActivity(name: string, group: UserActivityGroup, properties?: Properties): void {
    if (this.logToConsole) {
      console.info(`logUserActivity ${name} ${group} ${JSON.stringify(properties)}`);
    }

    this.logAppInsightsTrackEvent(name, {
      ...properties,
      EventName: name,
      EventGroup: group,
    });
  }

  /**
   * Logs an event to Application Insights with the specified name, properties, and optional measurements.
   *
   * @param name - The name of the event to log.
   * @param properties - A dictionary of properties to associate with the event.
   * @param measurements - An optional dictionary of measurements to associate with the event.
   */
  public logAppInsightsTrackEvent(name: string, properties: { [key: string]: any }, measurements?: { [name: string]: number }) {
    const updatedProps = this.telemetryPropertiesCheck(properties);
    this.appInsights.trackEvent({ name, properties: updatedProps, measurements });
  }

  /**
   * Checks and processes telemetry properties by removing invalid properties and capitalizing the first letter of each key.
   *
   * @param data - An object containing telemetry properties to be checked and processed.
   * @returns A new object with invalid properties removed and keys' first letters capitalized.
   */
  public telemetryPropertiesCheck(data: { [key: string]: any }): { [key: string]: any } {
    const updatedProps = TelemetryProvider.removeUglyTelemetryProperties(data);

    return this.getFirstLetterOfKeyCapitalizedAndLogImproperKeys(updatedProps);
  }

  /**
   * Processes the keys of the given data object by capitalizing the first letter of each key
   * and logging any keys that do not start with a capital letter.
   *
   * @param data - An object where the keys are strings and values can be of any type.
   * @returns A new object with the keys' first letters capitalized.
   */
  public getFirstLetterOfKeyCapitalizedAndLogImproperKeys(data: { [key: string]: any }): { [key: string]: any } {
    if (!data || Object.keys(data).length === 0) {
      return data;
    }

    const properKeys: { [key: string]: any } = {};
    const improperKeys: string[] = [];

    Object.keys(data).forEach((key) => {
      const excludedProperty = Array.from(TelemetryProvider.excludedProperties).some((item) => localeIgnoreCaseEquals(item, key));
      if (excludedProperty || key[0] === key[0].toUpperCase()) {
        properKeys[key] = data[key];
      } else {
        improperKeys.push(key);
        properKeys[key.charAt(0).toUpperCase() + key.slice(1)] = data[key];
      }
    });

    if (improperKeys.length > 0) {
      this.appInsights.trackEvent({
        name: 'ImproperKeys',
        properties: {
          EventName: 'ImproperKeys',
          ImproperKeys: improperKeys.join(', '),
          Message: 'Keys should start with a capital letter',
          Tag: 'Scuba',
        },
      });
    }

    return properKeys;
  }

  /**
   * Removes telemetry properties that do not start with an alphabet.
   *
   * @param data The telemetry data object.
   * @returns {object} The cleaned telemetry data object.
   */
  public static removeUglyTelemetryProperties(data: { [key: string]: any }): { [key: string]: any } {
    // Collect keys to remove based on whether the first character is not an alphabet
    const keysToRemove: string[] = [];
    Object.keys(data).forEach((key: string) => {
      // Check if the first character is not an alphabet
      if (!/^[a-zA-Z]/.test(key[0])) {
        keysToRemove.push(key);
      }
    });

    // Create a new object excluding the keys to remove
    const cleanedData = { ...data };
    keysToRemove.forEach((key) => {
      delete cleanedData[key];
    });

    // Return the modified map
    return cleanedData;
  }
}
